var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
const volumeModes = ['db', 'numeric'];
export function isVolumeMode(value) {
    return volumeModes.some(m => m === value);
}
export class Yamaha {
    constructor(http, state, log) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.receiverZone = 'main';
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.volumeMode = 'db';
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const data = (yield this.http.requestJson('get', '/YamahaExtendedControl/v1/system/getDeviceInfo', { tags }));
                return (_a = data === null || data === void 0 ? void 0 : data.system_version.toString()) !== null && _a !== void 0 ? _a : 'unknown';
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/setPower`, { params: { power: power ? 'on' : 'standby' }, tags });
                if (data.response_code !== 0) {
                    this.log.error('failed setting power');
                    return this.state.power.value;
                }
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return power;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${key}/getStatus`, { tags });
                if (data.response_code !== 0) {
                    this.log.error('failed getting videoMatrixRoutes');
                    return this.state.videoMatrixRoutes.values[key];
                }
                return data.input;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${key}/setInput`, { params: { input: value }, tags });
                if (data.response_code !== 0) {
                    this.log.warning('failed setting videoMatrixRoutes');
                    return this.state.videoMatrixRoutes.values[key];
                }
                return value;
            }),
        };
        this.volume = {
            range: { max: 16.5, min: -43.5, round: 0.5 },
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/setActualVolume`, { params: { mode: this.volumeMode, value: volume }, tags });
                if (data.response_code !== 0) {
                    this.log.error('failed setting volume');
                    return this.state.volume.value;
                }
                return volume;
            }),
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/setMute`, { params: { enable: mute }, tags });
                if (data.response_code !== 0) {
                    this.log.error('failed setting volumeMute');
                    return this.state.volumeMute.value;
                }
                return mute;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 2000,
                function: () => __awaiter(this, void 0, void 0, function* () { return yield this.getStatus(true); }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            // Set system volume units.
            const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/setActualVolume`, { params: { mode: this.volumeMode } });
            if (data.response_code !== 0)
                this.log.error('failed setting volume mode');
            yield this.getFeatures();
            yield this.getStatus();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/getStatus`);
                if ((data === null || data === void 0 ? void 0 : data.response_code) !== 0)
                    return 'verifyFailed';
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'connected';
        });
    }
    // other class methods
    getFeatures() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = (yield this.http.requestJson('get', '/YamahaExtendedControl/v1/system/getFeatures', { tags: ['getVolumeRange'] }));
            if (data.response_code !== 0) {
                this.log.error('failed getting features');
                return;
            }
            // Get volume range values from the main zone and update the range on the volume control.
            for (const myZone of data.zone) {
                if (myZone.id === this.receiverZone) {
                    if (myZone.actual_volume_mode_list.includes(this.volumeMode)) {
                        for (const myRange of myZone.range_step) {
                            if (myRange.id === `actual_volume_${this.volumeMode}`) {
                                const range = this.volume.range;
                                range.max = myRange.max;
                                const minV = range.max - 60;
                                range.min = minV >= myRange.min ? minV : myRange.min;
                                range.round = myRange.step;
                                this.log.debug(`volume range: ${JSON.stringify(this.volume.range)}`);
                                break;
                            }
                        }
                    }
                    else {
                        this.log.warning(`${this.volumeMode} is not supported in ${myZone.actual_volume_mode_list}`);
                    }
                    break;
                }
            }
        });
    }
    getStatus(isPoll = false) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? ['poll'] : [];
            const data = (yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/getStatus`, { tags }));
            if (data.response_code !== 0) {
                this.log.error('failed getting status');
                return;
            }
            this.state.power.update((data === null || data === void 0 ? void 0 : data.power) === 'on');
            const statusVolumeMode = (_a = data === null || data === void 0 ? void 0 : data.actual_volume.mode) !== null && _a !== void 0 ? _a : this.volumeMode;
            if (statusVolumeMode !== this.volumeMode) {
                this.log.debug(`resetting volume mode from ${statusVolumeMode} to ${this.volumeMode}`);
                const data = yield this.http.requestJson('get', `/YamahaExtendedControl/v1/${this.receiverZone}/setActualVolume`, { params: { mode: this.volumeMode }, tags });
                if (data.response_code !== 0)
                    this.log.error('failed setting volume mode');
            }
            this.state.volume.update((_b = data === null || data === void 0 ? void 0 : data.actual_volume.value) !== null && _b !== void 0 ? _b : 0);
            this.state.volumeMute.update((_c = data === null || data === void 0 ? void 0 : data.mute) !== null && _c !== void 0 ? _c : false);
        });
    }
}
// static properties
Yamaha.driverType = 'httpDriver';
Yamaha.key = '-M4F1s68VxDuXE3P4Cat';
Yamaha.httpRequestOptions = {
    prettyCommands: true,
    scheme: 'http',
};
